<template>
  <div
    class="compose-progress"
    :class="{ 'compose-progress-compact': compact }"
    role="status"
    aria-live="polite"
  >
    <div class="compose-organizer" aria-hidden="true">
      <span class="compose-source compose-source-one"></span>
      <span class="compose-source compose-source-two"></span>
      <span class="compose-source compose-source-three"></span>
      <span class="compose-thread compose-thread-one"></span>
      <span class="compose-thread compose-thread-two"></span>
      <span class="compose-thread compose-thread-three"></span>
      <span class="compose-line compose-line-one"></span>
      <span class="compose-line compose-line-two"></span>
      <span class="compose-line compose-line-three"></span>
    </div>

    <div class="compose-copy">
      <div class="compose-heading">
        <strong>Composing your <AiViewTerm /></strong>
        <span>{{ progressState.timeLabel }}</span>
      </div>
      <p>{{ progressState.stage }}</p>
      <div
        class="compose-track"
        role="progressbar"
        :aria-valuenow="progressState.percent"
        aria-valuemin="0"
        aria-valuemax="100"
        :aria-label="`${progressState.stage}. ${progressState.timeLabel}.`"
      >
        <span :style="{ transform: `scaleX(${progressState.progress})` }"></span>
      </div>
      <p v-if="!compact" class="compose-departure">
        <template v-if="surface === 'home'">You can keep exploring the site. Your <AiViewTerm /> will keep composing, and we&rsquo;ll let you know when it&rsquo;s ready.</template>
        <template v-else>You can close this panel. Your <AiViewTerm /> will keep composing, and we&rsquo;ll let you know when it&rsquo;s ready.</template>
      </p>
    </div>
  </div>
</template>

<script>
import AiViewTerm from '../ui/AiViewTerm.vue';
import { registerInlineProgress } from '../../utils/viewProgressPresence';
import { viewComposeState } from '../../utils/viewSite';
import { viewComposeProgress } from '../../utils/viewComposeProgress';

export default {
  name: 'ViewComposeProgress',
  components: { AiViewTerm },
  props: {
    surface: { type: String, default: 'panel' },
    startedAt: { type: Number, default: 0 },
    compact: { type: Boolean, default: false },
    inline: { type: Boolean, default: true },
  },
  data() {
    return { now: Date.now(), timer: null };
  },
  computed: {
    progressState() {
      return viewComposeProgress(this.startedAt, this.now, viewComposeState.progress);
    },
  },
  mounted() {
    if (this.inline) this._presence = registerInlineProgress(this.$el);
    this.timer = window.setInterval(() => {
      this.now = Date.now();
      this._presence?.refresh();
    }, 250);
  },
  beforeUnmount() {
    window.clearInterval(this.timer);
    this._presence?.dispose();
  },
};
</script>

<style scoped>
.compose-progress {
  box-sizing: border-box;
  display: grid;
  grid-template-columns: 8.5rem minmax(0, 1fr);
  align-items: center;
  gap: clamp(0.8rem, 1.8vw, 1.1rem);
  padding: clamp(1.2rem, 3.5vw, 1.65rem);
  container-type: inline-size;
  color: var(--site-accent-ink, #3d382f);
  background: color-mix(in srgb, var(--site-accent, #9a7840) 7%, transparent);
  border-radius: 14px;
  font-family: "Quattrocento Sans", sans-serif;
  overflow: hidden;
}
.compose-organizer {
  position: relative;
  width: 8.5rem;
  height: 5.8rem;
  isolation: isolate;
}
.compose-source,
.compose-line,
.compose-thread {
  position: absolute;
  display: block;
}
.compose-source {
  left: 0.2rem;
  width: 2.5rem;
  height: 1.35rem;
  border: 1px solid color-mix(in srgb, var(--site-accent, #9a7840) 27%, transparent);
  background: color-mix(in srgb, var(--site-accent, #9a7840) 9%, transparent);
  box-shadow: 0 5px 14px color-mix(in srgb, var(--site-accent, #9a7840) 9%, transparent);
  animation: compose-source-float 3.8s cubic-bezier(0.37, 0, 0.2, 1) infinite alternate;
}
.compose-source-one { top: 0.3rem; transform: rotate(-5deg); }
.compose-source-two { top: 2.2rem; left: 0.55rem; animation-delay: -1.2s; transform: rotate(3deg); }
.compose-source-three { top: 4.05rem; left: 0.12rem; animation-delay: -2.1s; transform: rotate(-2deg); }
.compose-thread {
  left: 2.55rem;
  width: 3rem;
  height: 1px;
  transform-origin: left center;
  background: linear-gradient(90deg, color-mix(in srgb, var(--site-accent, #9a7840) 52%, transparent), transparent 88%);
  animation: compose-thread-flow 2.4s ease-in-out infinite;
}
.compose-thread-one { top: 1rem; transform: rotate(15deg); }
.compose-thread-two { top: 2.9rem; animation-delay: -0.8s; }
.compose-thread-three { top: 4.75rem; animation-delay: -1.6s; transform: rotate(-15deg); }
.compose-line {
  right: 0.15rem;
  width: 2.8rem;
  height: 0.48rem;
  border-radius: 999px;
  background: color-mix(in srgb, var(--site-accent, #9a7840) 21%, transparent);
  transform-origin: left center;
  animation: compose-line-settle 2.8s cubic-bezier(0.22, 1, 0.36, 1) infinite alternate;
}
.compose-line-one { top: 1.05rem; }
.compose-line-two { top: 2.7rem; width: 2.15rem; animation-delay: -0.9s; }
.compose-line-three { top: 4.35rem; width: 2.55rem; animation-delay: -1.8s; }
.compose-copy { min-width: 0; }
.compose-heading {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 1rem;
}
.compose-heading strong {
  color: inherit;
  font-size: 1rem;
  font-weight: 600;
  letter-spacing: -0.012em;
}
.compose-heading > span {
  flex: 0 0 auto;
  color: var(--site-accent-muted, #736957);
  font-size: 0.72rem;
  font-variant-numeric: tabular-nums;
}
.compose-copy > p {
  margin: 0.35rem 0 0;
  color: var(--site-accent-muted, #736957);
  font-size: 0.79rem;
  line-height: 1.45;
}
.compose-track {
  height: 3px;
  margin-top: 0.95rem;
  overflow: hidden;
  border-radius: 999px;
  background: color-mix(in srgb, var(--site-accent, #9a7840) 13%, transparent);
}
.compose-track > span {
  display: block;
  width: 100%;
  height: 100%;
  border-radius: inherit;
  transform-origin: left center;
  background: color-mix(in srgb, var(--site-accent, #9a7840) 68%, #6e5735);
  transition: transform 520ms cubic-bezier(0.22, 1, 0.36, 1);
}
.compose-copy .compose-departure {
  margin-top: 0.75rem;
  max-width: 34rem;
  color: color-mix(in srgb, var(--site-accent-ink, #3d382f) 66%, transparent);
  font-size: 0.72rem;
}
.compose-progress-compact {
  grid-template-columns: 4.7rem minmax(0, 1fr);
  width: min(26rem, calc(100vw - 2rem));
  padding: 0.9rem 1.4rem 0.95rem 0.85rem;
  border-radius: 12px;
  background: transparent;
}
.compose-progress-compact .compose-organizer { width: 4.7rem; height: 3.25rem; transform: scale(0.55); transform-origin: left center; }
.compose-progress-compact .compose-heading { flex-wrap:wrap;column-gap:.6rem;row-gap:.15rem; }
.compose-progress-compact .compose-heading strong { font-size: 0.86rem;white-space:nowrap; }
.compose-progress-compact .compose-heading > span { font-size: 0.66rem; }
.compose-progress-compact .compose-copy > p { font-size: 0.7rem; }
.compose-progress-compact .compose-track { margin-top: 0.55rem; }
@keyframes compose-source-float {
  0% { opacity: 0.62; translate: 0 0; }
  100% { opacity: 1; translate: 0.38rem 0; }
}
@keyframes compose-thread-flow {
  0%, 100% { opacity: 0.24; scale: 0.72 1; }
  50% { opacity: 0.76; scale: 1 1; }
}
@keyframes compose-line-settle {
  0% { opacity: 0.46; scale: 0.72 1; translate: 0.28rem 0; }
  100% { opacity: 1; scale: 1 1; translate: 0 0; }
}
:global(body.dark-mode .compose-progress) {
  color: var(--site-accent-ink);
  background: color-mix(in srgb, var(--site-accent) 10%, transparent);
}
:global(body.dark-mode .compose-progress-compact) { background: transparent; }
@media (max-width: 520px) {
  .compose-progress-compact { grid-template-columns: 3.9rem minmax(0, 1fr); }
  .compose-progress-compact .compose-organizer { width: 4rem; }
  .compose-progress-compact .compose-heading { flex-direction: row; align-items: baseline; gap: 0.7rem; }
}

/* Narrow containers (the homepage Ask panel at desktop widths, phones):
   stack the animation above the copy instead of squeezing the text column. */
@container (max-width: 480px) {
  .compose-progress:not(.compose-progress-compact) {
    grid-template-columns: 1fr;
    align-items: start;
    gap: 0.9rem;
    padding: clamp(1rem, 3.2vw, 1.25rem);
  }
  .compose-progress:not(.compose-progress-compact) .compose-organizer {
    width: 8.5rem;
    height: 5.8rem;
    margin: 0 auto;
  }
  .compose-progress:not(.compose-progress-compact) .compose-heading {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.22rem;
  }
  .compose-progress:not(.compose-progress-compact) .compose-copy > p {
    font-size: 0.76rem;
  }
  .compose-progress:not(.compose-progress-compact) .compose-departure {
    max-width: none;
  }
}
@media (prefers-reduced-motion: reduce) {
  .compose-source,
  .compose-thread,
  .compose-line { animation: none; }
  .compose-track > span { transition-duration: 100ms; }
}
</style>
